import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import ScaleContext from "../../../context/scale/context";
import { IScale } from "../../../models/scale/response";
import Space from "../../general/antd/space";
import Button from "../../general/antd/button";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import Can from "../../../utils/rbac/can";
import { Permissions } from "../../../utils/Permissions/Permissions";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import styles from "./styles.module.scss";

interface IProps {}

const ScaleTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, pendingSummaries, actions, loading, query } =
    useContext(ScaleContext);

  const openInNewTabOrNavigate = (url: string, e: any) => {
    if (e.ctrlKey || e.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const columns: ColumnProps<IScale>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 80,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      sorter: {
        multiple: 2,
      },
      width: 200,
      render: (name: string, record) => {
        // The one signal that matters on this page: the device is printing
        // something the platform no longer agrees with.
        const summary = pendingSummaries?.[record?.id];

        if (!summary?.hasPendingChanges) return name;

        return (
          <Tooltip title={`${t("scale_pending_changes")} (${summary.count})`}>
            <Badge count={summary.count} size="small" offset={[10, 0]}>
              <Space>
                <WarningOutlined style={{ color: "#faad14" }} />
                <span>{name}</span>
              </Space>
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      title: t("store"),
      dataIndex: "store",
      align: "center",
      key: "store",
      width: 180,
      render: (_: any, record) => record?.store?.name ?? "-",
    },
    {
      title: t("slots_count"),
      dataIndex: "slotsCount",
      align: "center",
      key: "slotsCount",
      sorter: {
        multiple: 3,
      },
      width: 130,
      // A scale with no capacity cannot be exported at all, so it is called out
      // here rather than only when the download fails.
      render: (slotsCount: number, record) =>
        slotsCount > 0 ? (
          `${record?.productsCount ?? 0} / ${slotsCount}`
        ) : (
          <Tooltip title={t("scale_capacity_not_set_hint")}>
            <Tag icon={<WarningOutlined />} color="error">
              {t("scale_capacity_not_set")}
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: t("department"),
      dataIndex: "department",
      align: "center",
      key: "department",
      width: 110,
      render: (department: number) => String(department ?? 0).padStart(2, "0"),
    },
    {
      title: t("scale_last_fed_at"),
      dataIndex: "lastExportedAt",
      align: "center",
      key: "lastExportedAt",
      width: 170,
      render: (value: string | null) =>
        value ? new Date(value).toLocaleString() : t("scale_never_fed"),
    },
    {
      title: t("is_active"),
      dataIndex: "isActive",
      align: "center",
      key: "isActive",
      width: 110,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("active")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("inactive")}
          </Tag>
        ),
    },
    {
      title: t("actions"),
      width: 220,
      dataIndex: "",
      align: "center",
      key: "actions",
      render: (_: any, record) => (
        <Space>
          <Tooltip title={t("scale_slots")}>
            <Button
              shape="circle"
              icon={<UnorderedListOutlined />}
              onClick={(e) =>
                openInNewTabOrNavigate(`/scales/${record?.id}/slots`, e)
              }
            />
          </Tooltip>
          <ViewBtn
            onClick={(e) =>
              openInNewTabOrNavigate(`/scales/details/${record?.id}`, e)
            }
          />
          {/* Defining a device and its capacity is installation work, not daily
              work, so these two sit behind their own permissions. */}
          <Can perform={Permissions.CAN_UPDATE_SCALE}>
            <EditBtn
              onClick={(e) =>
                openInNewTabOrNavigate(`/scales/update/${record?.id}`, e)
              }
            />
          </Can>
          <Can perform={Permissions.CAN_DELETE_SCALE}>
            {/* Feeding replaces the scale table wholesale, so a scale that still
                holds products cannot be dropped: the backend rejects it with 422. */}
            {record?.productsCount > 0 ? (
              <Tooltip title={t("remove_scale_products_first")}>
                <span>
                  <DeleteBtn disabled onConfirm={() => undefined} />
                </span>
              </Tooltip>
            ) : (
              <DeleteBtn
                onConfirm={async () => {
                  await actions.deleteScale(record?.id);
                }}
                loading={loading.includes("delete")}
              />
            )}
          </Can>
        </Space>
      ),
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  // The count needs every slot of every scale, so it is one extra call for the
  // rows on screen rather than a field the listing could carry cheaply.
  useEffect(() => {
    const scaleIds = list?.data?.map((scale) => scale?.id) ?? [];
    scaleIds.length > 0 && actions.getListPendingChanges(scaleIds);
  }, [list]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        className={styles.table}
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default ScaleTable;
